import Combobox from "./combobox_controller"

// "Prevádzka" field. Branch rows repeating the same name are one group here, so
// the technician first picks the branch and then the address — and it is that
// address which decides the branch row the inspection is saved against.
export default class extends Combobox {
  static targets = ["addressList", "newAddressOption", "addressArrow", "contactName", "email", "phone", "note"]
  static outlets = ["customer-select", "address-fields"]
  static values = { pickAddressHint: String }

  connect() {
    super.connect()
    this.hideAddressesOnOutsideClick = (event) => {
      if (!this.addressListTarget.parentElement.contains(event.target)) this.closeAddresses()
    }
    document.addEventListener("click", this.hideAddressesOnOutsideClick)
    if (this.hiddenTarget.value) requestAnimationFrame(() => this.restoreGroup())
  }

  disconnect() {
    super.disconnect()
    document.removeEventListener("click", this.hideAddressesOnOutsideClick)
  }

  get hint() {
    if (this.inputTarget.value.trim() === "") return ""
    if (!this.group) return this.hiddenTarget.value ? this.existingHintValue : this.newHintValue
    if (this.hiddenTarget.value || this.addingAddress) return this.group.hint

    return `${this.group.hint} ${this.pickAddressHintValue}`
  }

  optionSubtitle(item) {
    return item.subtitle
  }

  // Without a company the list spans every customer, so picking a branch also
  // says which company the inspection belongs to.
  appendParams(url) {
    const customerId = this.hasCustomerSelectOutlet ? this.customerSelectOutlet.customerId : ""
    if (customerId) url.searchParams.set("q[customer_id_eq]", customerId)
  }

  // Editing an inspection starts with a branch already picked, so look its group
  // up to offer the sibling addresses without picking the branch again.
  async restoreGroup() {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q[name_cont]", this.inputTarget.value.trim())
    this.appendParams(url)

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    const { results } = await response.json()
    const group = results.find(({ branches }) => branches.some(({ id }) => id === this.hiddenTarget.value))
    if (!group) return

    this.group = group
    this.renderAddresses()
    this.refresh()
  }

  applySelection(group) {
    this.group = group
    this.addingAddress = false
    if (this.hasCustomerSelectOutlet) this.customerSelectOutlet.assign(group.customer)
    this.renderAddresses()

    group.branches.length === 1 ? this.pick(group.branches[0]) : this.clearRow()
  }

  detach() {
    this.group = null
    this.addingAddress = false
    this.hiddenTarget.value = ""
    this.renderAddresses()
    this.refresh()
  }

  clear() {
    this.detach()
    this.clearRow()
  }

  // Cascaded from the "Firma" field when a different company is picked or cleared.
  clearAll() {
    this.inputTarget.value = ""
    this.clear()
  }

  toggleAddresses(event) {
    event.stopPropagation()
    this.closeList()
    this.addressListTarget.classList.toggle("hidden")
  }

  closeAddresses() {
    this.addressListTarget.classList.add("hidden")
  }

  newAddress() {
    this.clearRow()
    this.addingAddress = true
    this.refresh()
    this.closeAddresses()
    this.addressFieldsOutlet.open()
  }

  renderAddresses() {
    this.addressListTarget.querySelectorAll('[data-role="option"]').forEach((option) => option.remove())
    this.addressArrowTarget.classList.toggle("hidden", !this.group)
    this.closeAddresses()
    if (!this.group) return

    this.group.branches.forEach((branch) => {
      const option = this.line(branch.address?.line || "", "px-4 py-2 cursor-pointer hover:bg-gray-50 text-sm text-[#1c1b1f]")
      option.dataset.role = "option"
      option.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.pick(branch)
        this.closeAddresses()
      })
      this.addressListTarget.insertBefore(option, this.newAddressOptionTarget)
    })
  }

  pick(branch) {
    this.addingAddress = false
    this.hiddenTarget.value = branch.id
    this.addressFieldsOutlet.assign(branch.address)
    this.writeContact(branch)
    this.refresh()
  }

  // Drop the stored row while staying inside the group: another address of this
  // prevádzka is about to be filled in.
  clearRow() {
    this.hiddenTarget.value = ""
    if (this.hasAddressFieldsOutlet) this.addressFieldsOutlet.assign(null)
    this.writeContact({})
    this.refresh()
  }

  writeContact(branch) {
    this.contactNameTarget.value = branch.contact_name || ""
    this.emailTarget.value = branch.email || ""
    this.phoneTarget.value = branch.phone || ""
    this.noteTarget.value = branch.note || ""
  }
}
